import { IClinic, IProduct } from "@interfaces";
import React, { ChangeEvent, memo, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import {
  Input,
  Form,
  Row,
  Col,
  FormInstance,
  Button,
  Table,
  Space,
  Select,
  Layout,
  Spin,
  Modal,
} from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";
import { PATH_CLINIC, discountOptions, enumDiscount } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

import { useNotify, getApiErrorMessage } from "@utils";
import { useHistory } from "react-router";
import { DeleteIcon, SharedButton } from "@components";
import styled from "styled-components";
import { ColumnsType } from "antd/es/table";
const { Search } = Input;

interface IProps {
  product?: IProduct;
}
const tableData: IProduct[] = [
  {
    id: 1,
    productName: "Paracetamol",
    sku: "0101",
    quality: 32,
    pricePerUnit: "1,000",
    pricePerQuality: "1,000",
    discountType: "",
    discountAmount: 0,
    discountPrice: 0,
    totalAmount: 100,
  },
  {
    id: 2,
    productName: "Paracetamol",
    sku: "0101",
    quality: 32,
    pricePerUnit: "1000",
    pricePerQuality: "1000",
    discountType: "",
    discountAmount: 0,
    discountPrice: 0,
    totalAmount: 100,
  },
];
const initValue: Partial<IProduct> = {
  productName: "",
  mainGroup: "",
  subGroup: "",
  sku: "",
  strengthValue: "",
  category: "",
  subCategory: "",
  packageSize: 0,
  authorizedDistributer: "",
  minPruchaseQty: 0,
  purchaseIncreaseCount: 0,
  sellingUnit: "",
  sellingPrice: 0,
  manufacturerName: "",
  madeInCountry: "",
  chemicalName: "",
  actionAndIndication: "",
  dosage: "",
  potentialSideEffect: "",
  productDescription: "",
  productImage: "",
};
interface ITotal {
  gross: number;
  discount: number;
  net: number;
}
const totalInit: ITotal = { gross: 0, discount: 0, net: 0 };
export const OrderForm = memo(({ product }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupProduct } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const productSchema = Yup.object().shape(YupProduct);
  const [data, setData] = useState<IProduct>(initValue);
  const [clinicForm] = Form.useForm();
  const [result, addResult] = useState(tableData);
  const [updating, updateTable] = useState(false);
  const [modalPromo, setModalPromo] = useState(false);
  const [item, setItem] = useState<IProduct | null>(null);
  const [promo, updatePromo] = useState(0);
  const [total, updateTotal] = useState<ITotal>(totalInit);
  useEffect(() => {
    if (product) {
      setData({ ...data, ...product });
      formRef.current?.setFieldsValue({
        ...data,
        ...product,
      });
    }
  }, [product]);

  useEffect(() => {
    if (result) {
      const res = result.reduce(
        (acc, cur) => {
          acc["gross"] = acc.gross + (cur?.totalAmount || 0);
          acc["discount"] = acc.discount + (cur?.discountAmount || 0);
          acc["net"] = acc.net + ((cur?.totalAmount || 0) - (cur?.discountAmount || 0));
          return acc;
        },
        { gross: 0, discount: 0, net: 0 }
      );
      updateTotal(res);
    }
  }, [result]);

  const onFinish = async (values: IClinic) => {
    try {
      dispatch(setLoading(true));

      await console.log(values);
      success(t("message.create.success"));
      history.push(PATH_CLINIC);
    } catch (err: any) {
      error(getApiErrorMessage(err, t("message.create.fail")));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await productSchema.validateSyncAt(field, { [field]: value });
    },
  };

  const goBack = () => history.goBack();

  const updateTableItem = async (type: any, discount: number, record: IProduct) => {
    try {
      await updateTable(true);
      const currentResult = result;
      const index = currentResult.findIndex((obj) => obj.id === record.id);
      let updated = {};
      if (type) {
        updated = { ...record, discountType: type };
      }
      if (discount) {
        updated = {
          ...record,
          discountAmount: discount,
          discountPrice:
            record?.discountType === enumDiscount.PERCENTAGE
              ? Number((discount / (record?.totalAmount || 0)) * 100)
              : discount,
          totalAmount:
            record?.discountType === enumDiscount.PERCENTAGE
              ? (record?.totalAmount || 0) - Number((discount / (record?.totalAmount || 0)) * 100) // calculate percentage
              : (record?.totalAmount || 0) - discount,
        };
      }
      currentResult[index] = { ...currentResult[index], ...updated };
      await addResult(currentResult);
      await updateTable(false);
    } catch (error: any) {
      await updateTable(false);
      console.log(error);
    } finally {
      await updateTable(false);
    }
  };
  const handleUpdatePromo = async (record: IProduct) => {
    await updateTableItem("", promo, record);
    await updateTotalAmount();
    await setModalPromo(false);
  };
  const selectPromoType = async (value: any, record: IProduct) => {
    await updateTableItem(value, 0, record);
  };
  const updateTotalAmount = () => {
    if (result) {
      const res = result.reduce(
        (acc, cur) => {
          acc["gross"] = acc.gross + (cur?.totalAmount || 0);
          acc["discount"] =
            acc.discount +
            (cur?.discountType === enumDiscount.PERCENTAGE
              ? Number(((cur?.discountAmount || 0) / (cur?.totalAmount || 0)) * 100)
              : cur?.discountAmount || 0);
          acc["net"] = acc.net + (cur?.totalAmount || 0);
          return acc;
        },
        { gross: 0, discount: 0, net: 0 }
      );
      updateTotal(res);
    }
  };

  const columns: ColumnsType<IProduct> = [
    {
      title: t("page.sku_code"),
      dataIndex: "sku",
      key: "1",
    },
    {
      title: t("page.product_name"),
      dataIndex: "productName",
      key: "2",
    },
    {
      title: t("page.quality"),
      dataIndex: "quality",
      key: "3",
    },
    {
      title: t("page.price_per_unit"),
      dataIndex: "pricePerUnit",
      key: "4",
    },
    {
      title: t("page.price_per_quality"),
      dataIndex: "pricePerQuality",
      key: "5",
    },
    {
      title: t("page.discount_type"),
      dataIndex: "discountType",
      key: "6",
      render: (_, record) => (
        <Space size="middle">
          <StyledSelect
            value={record.discountType}
            placeholder={t("page.choose_discount")}
            onChange={(val) => selectPromoType(val, record)}
          >
            {discountOptions.map((item) => (
              <Select.Option key={item} value={item}>
                {t(`page.${item}`)}
              </Select.Option>
            ))}
          </StyledSelect>
        </Space>
      ),
    },
    {
      title: t("page.discount_amount"),
      key: "7",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setModalPromo(!modalPromo);
              setItem(record);
            }}
            disabled={!record?.discountType}
          >
            {record?.discountAmount ? record?.discountAmount : t("page.add_discount")}
            {record?.discountType === enumDiscount.PERCENTAGE ? "%" : "MMK"}
          </Button>
        </Space>
      ),
    },
    {
      title: t("page.discount_price"),
      dataIndex: "discountPrice",
      key: "8",
    },
    {
      title: t("page.total_amount"),
      dataIndex: "totalAmount",
      key: "9",
    },
    {
      title: t("page.actions"),
      key: "10",
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" danger icon={<DeleteIcon />} size={"small"} />
          <span>{t("page.delete")}</span>
        </Space>
      ),
    },
  ];

  const handleSearch = (value: any) => console.log(value);

  return (
    <>
      <Form
        form={clinicForm}
        name="basic"
        ref={formRef}
        initialValues={data}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
      >
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.provider_name")} name="productName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.provider")} name="mainGroup" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.phone_number")} name="productName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.delivery_address")} name="mainGroup" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>

        <StyledLayout>
          <div className="search-layout">
            <p>{t("page.search_product")}</p>
            <Search
              placeholder="input search text"
              allowClear
              enterButton="Add Product"
              size="large"
              onSearch={handleSearch}
            />
          </div>
          <div>
            {updating ? (
              <Spin />
            ) : (
              <StyledTable
                columns={columns}
                dataSource={result}
                loading={updating}
                pagination={false}
              />
            )}
          </div>
        </StyledLayout>
        <StyledTotal>
          <div className="total-layout">
            <div className="item">
              <p className="label">{t("page.gross_amount")}</p>
              <p className="value">{total?.gross} MMK</p>
            </div>
            <div className="item">
              <p className="label">{t("page.discount")}</p>
              <p className="value">{total?.discount.toFixed()} MMK</p>
            </div>
            <div className="item">
              <p className="label">{t("page.net_amount")}</p>
              <p className="value">{total?.net} MMK</p>
            </div>
          </div>
        </StyledTotal>

        <Form.Item>
          <StyledFormAction>
            <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
              {t("page.cancel")}
            </SharedButton>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.create")}
            </SharedButton>
          </StyledFormAction>
        </Form.Item>
      </Form>
      <Modal
        title={t("page.add_promo")}
        open={modalPromo}
        onCancel={() => {
          setItem(null);
          setModalPromo(false);
        }}
        onOk={() => item && handleUpdatePromo(item)}
      >
        <StyledInput
          type="number"
          suffix={item?.discountType === enumDiscount.PERCENTAGE ? "%" : "MMK"}
          onChange={(e) => updatePromo(Number(e.target.value))}
          // maxLength={item?.discountType === enumDiscount.PERCENTAGE ? 3 : 10}
        />
      </Modal>
    </>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead .ant-table-cell {
    background-color: #8090a9;
    color: #fff;
  }
`;

const StyledSelect = styled(Select)`
  min-width: 80px;
`;
const StyledInput = styled(Input)`
  min-width: 70px;
`;
const StyledLayout = styled(Layout)`
  .search-layout {
    display: flex;
    align-items: center;
    column-gap: 30px;
    margin-bottom: 20px;
    width: 60%;
    .ant-input-search {
      flex: 7;
    }
  }
`;
const StyledTotal = styled(Layout)`
  display: flex;
  justify-content: end;
  flex-direction: row;
  .total-layout {
    background-color: #fff;
    min-width: 326px;
    margin: 16px 0;
    .item {
      padding: 16px;
      border-bottom: 1px solid #eee;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .label {
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }
      .value {
      }
    }
  }
`;
